import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';
// Side-effect import: guarantees the FYD fractional custom elements are defined even when the
// product page first paints on a non-FYD (e.g. Fat Quarter / FFQ) variant. Without this, a
// switch INTO an FYD variant would morph in an <fractional-quantity-selector-component> that
// never upgrades.
import '@theme/fractional-quantity';

/**
 * FYD quantity reconciler.
 *
 * A fabric product can mix variants of different KINDS under one option — fabric yardage (SKU
 * `FYD`, rendered as the fractional-yard `<fractional-quantity-selector-component>`) and fat
 * quarter (SKU `FFQ`, or any non-FYD variant, rendered as the plain
 * `<quantity-selector-component>`). On a normal product page the variant picker only morphs the
 * `<variant-picker>` element itself and dispatches `StandardEvents.productSelect`; the quantity
 * selector is otherwise only patched in place (min/max/step) by `product-form-component`, which
 * never swaps the selector's ELEMENT TYPE. So switching between an FYD and a non-FYD variant
 * would leave the wrong selector on screen.
 *
 * This element wraps the product-page quantity block (mounted from `blocks/quantity.liquid`, only
 * for multi-variant products that have at least one FYD variant). On `product:select` it swaps its
 * subtree to the freshly server-rendered quantity selector carried on the event — but ONLY when
 * the selector kind actually changes. Same-kind switches (FYD→FYD, plain→plain) are left untouched
 * so the shopper's chosen quantity is preserved, matching `product-form-component`'s behavior. On a
 * kind change the value resets to the new variant's server default (1 yd for FYD, 1 for a fat
 * quarter), which is what taking the fresh server HTML gives us.
 *
 * `assets/component.js`'s `Component` base re-syncs its `refs` via a MutationObserver on any
 * descendant change, so `product-form-component.refs.quantitySelector` updates itself after the
 * swap — no stale references.
 *
 * Horizon v4 note: variant changes used to emit `ThemeEvents.variantUpdate` (`variant:update`).
 * That event was removed in favor of `@shopify/events` `StandardEvents.productSelect`; this
 * component must await `event.promise` for the section HTML (same pattern as `product-price.js`).
 *
 * See docs/plans/0004-fractional-quantity.md.
 */
class FydQuantityMount extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #onProductSelect = (event) => {
    // Ignore product-card / quick-add picks that bubble into the same section.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct, productId } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (productId && this.dataset.productId && productId !== this.dataset.productId) {
          return;
        }

        const freshMount = html.querySelector('fyd-quantity-mount');
        if (!freshMount) return;

        // Compare the current and incoming quantity selector element types. Only reconcile when the
        // kind changes (FYD fractional <-> plain integer); a same-kind switch keeps the chosen value.
        const currentTag = this.querySelector('[ref="quantitySelector"]')?.tagName;
        const freshTag = freshMount.querySelector('[ref="quantitySelector"]')?.tagName;
        if (!freshTag || currentTag === freshTag) return;

        morph(this, freshMount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[fyd-quantity-mount] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('fyd-quantity-mount')) {
  customElements.define('fyd-quantity-mount', FydQuantityMount);
}
